import { Stack, Typography } from "@mui/material";
import { AppRoutes } from "../../types/enums";
import { Engagement } from "../../types";
import { EllipsisTooltip } from "../ui/CustomTooltip";
import React, { ReactNode } from "react";
import { mockEngagementScore } from "../../types/genereal";
import { Link, useParams } from "react-router-dom";
import { indicatorMapper, RateIndicatorIcon } from "../ui/RateIndicatorIcon";

const ItemWrapper = ({
  index,
  name,
  penetration_score,
  total_engagement_score,
  total_calls,
  total_calls_responded,
  total_emails,
  total_emails_responded,
  total_meetings,
  header = false,
}: {
  index: ReactNode;
  name: ReactNode;
  penetration_score: ReactNode;
  total_engagement_score: ReactNode;
  total_calls: ReactNode;
  total_calls_responded: ReactNode;
  total_emails: ReactNode;
  total_emails_responded: ReactNode;
  total_meetings: ReactNode;
  header?: boolean;
}) => {
  return (
    <>
      <Stack minWidth="30px" maxWidth="4%" width="100%">
        {index}
      </Stack>
      <Stack minWidth="160px" maxWidth="30%" width="100%">
        {name}
      </Stack>
      <Stack minWidth="60px" maxWidth="15%" width="100%">
        {penetration_score}
      </Stack>
      <Stack minWidth="60px" maxWidth="15%" width="100%">
        {total_engagement_score}
      </Stack>
      {header ? (
        <Stack
          minWidth="55px"
          maxWidth="Calc(12% + 16px)"
          width="100%"
          alignItems="center"
          paddingRight="2%"
        >
          Calls
        </Stack>
      ) : (
        <>
          <Stack minWidth="55px" maxWidth="6%" width="100%">
            {total_calls}
          </Stack>
          <Stack minWidth="55px" maxWidth="6%" width="100%">
            {total_calls_responded}
          </Stack>
        </>
      )}
      {header ? (
        <Stack
          minWidth="55px"
          maxWidth="Calc(12% + 16px)"
          width="100%"
          alignItems="center"
          paddingRight="3%"
        >
          Emails
        </Stack>
      ) : (
        <>
          <Stack minWidth="45px" maxWidth="6%" width="100%">
            {total_emails}
          </Stack>
          <Stack minWidth="55px" maxWidth="6%" width="100%">
            {total_emails_responded}
          </Stack>
        </>
      )}

      <Stack minWidth="80px" maxWidth="6%" width="100%">
        {total_meetings}
      </Stack>
    </>
  );
};

const ItemList = ({
  id,
  index,
  name,
  total_calls,
  total_calls_responded,
  total_emails,
  total_emails_responded,
  total_meetings,
  engagement,
  discovery,
}: Engagement) => {
  const { campaign_id } = useParams();
  return (
    <Link
      onClick={(e) => name === "--" && e.preventDefault()}
      to={`${AppRoutes.ACCOUNT}/${campaign_id}/${id}`}
      style={{ textDecoration: "none" }}
    >
      <Stack
        sx={{
          border: "1px solid #E0E0E0",
          padding: "14px 16px",
          borderRadius: "8px",
          flexWrap: "nowrap",
          flexDirection: "row",
          gap: "12px",
        }}
      >
        <ItemWrapper
          index={
            <Stack direction="row" gap="4px" alignItems="center">
              <Typography
                variant="body1"
                sx={{
                  color: "#1F2932",
                  minWidth: "10px",
                }}
              >
                {index}
              </Typography>
            </Stack>
          }
          name={
            <Typography
              variant="body1"
              sx={{
                color: "#1F2932",
                paddingRight: "16px",
              }}
            >
              {name === null ? "--" : name}
            </Typography>
          }
          penetration_score={
            <Stack
              alignItems="center"
              flexDirection="row"
              gap="8px"
              justifyContent="flex-end"
            >
              <RateIndicatorIcon
                indicatorRate={discovery.indicator}
                indicatorChangeAt={discovery.changed_at}
                indicatorChange={discovery.change}
              />
              <Typography
                variant="body1"
                sx={{
                  color: "#1F2932",
                  ...(discovery.indicator && {
                    color: indicatorMapper[discovery.indicator].color,
                  }),
                }}
              >
                {discovery.score === null ? "--" : `${discovery.score}%`}
              </Typography>
            </Stack>
          }
          total_engagement_score={
            <Stack
              alignItems="center"
              flexDirection="row"
              gap="8px"
              justifyContent="flex-end"
              paddingRight="16px"
            >
              <RateIndicatorIcon
                indicatorRate={engagement.indicator}
                indicatorChange={engagement.change}
                indicatorChangeAt={engagement.changed_at}
              />
              <Typography
                variant="body1"
                sx={{
                  color: "#1F2932",
                  ...(engagement.indicator && {
                    color: indicatorMapper[engagement.indicator].color,
                  }),
                }}
              >
                {engagement.score === null ? "--" : `${engagement.score}%`}
              </Typography>
            </Stack>
          }
          total_calls={
            <Typography
              variant="body1"
              sx={{
                color: "#1F2932",
              }}
            >
              {total_calls === null ? "--" : total_calls}
            </Typography>
          }
          total_calls_responded={
            <Typography
              variant="body1"
              sx={{
                color: "#1F2932",
              }}
            >
              {total_calls_responded === null ? "--" : total_calls_responded}
            </Typography>
          }
          total_emails={
            <Typography
              variant="body1"
              sx={{
                color: "#1F2932",
              }}
            >
              {total_emails === null ? "--" : total_emails}
            </Typography>
          }
          total_emails_responded={
            <Typography
              variant="body1"
              sx={{
                color: "#1F2932",
              }}
            >
              {total_emails_responded === null ? "--" : total_emails_responded}
            </Typography>
          }
          total_meetings={
            <Typography
              variant="body1"
              sx={{
                color: "#1F2932",
              }}
            >
              {total_meetings === null ? "--" : total_meetings}
            </Typography>
          }
        />
      </Stack>
    </Link>
  );
};

type Props = {
  data: Engagement[];
};

export const EngagementScoreTable = ({ data }: Props) => {
  return (
    <Stack gap="8px" paddingY="24px">
      <Stack>
        <Stack
          sx={{
            backgroundColor: "#F1F1F1",
            borderTopRightRadius: "8px",
            borderTopLeftRadius: "8px",
            flexDirection: "row",
            gap: "12px",
            flexWrap: "nowrap",
            padding: "10px 16px",
          }}
        >
          <ItemWrapper
            header={true}
            index={""}
            name={""}
            penetration_score={""}
            total_engagement_score={""}
            total_calls={""}
            total_calls_responded={""}
            total_emails={""}
            total_emails_responded={""}
            total_meetings={"Meetings"}
          />
        </Stack>
        <Stack
          sx={{
            backgroundColor: "#F7F7F7",
            borderBottomRightRadius: "8px",
            borderBottomLeftRadius: "8px",
            flexDirection: "row",
            gap: "12px",
            flexWrap: "nowrap",
            padding: "10px 16px",
          }}
        >
          <ItemWrapper
            index={""}
            name={
              <Typography
                variant="body1"
                noWrap={true}
                sx={{
                  color: "#697184",
                }}
              >
                Name
              </Typography>
            }
            penetration_score={
              <Stack
                alignItems="center"
                flexDirection="row"
                gap="8px"
                justifyContent="flex-end"
              >
                <EllipsisTooltip
                  tooltip="Rate of potential BC members, identified
              in available data, to expected BC size."
                >
                  <Typography
                    variant="body1"
                    noWrap={true}
                    sx={{
                      color: "#697184",
                    }}
                  >
                    Discovery
                  </Typography>
                </EllipsisTooltip>
              </Stack>
            }
            total_engagement_score={
              <Stack
                alignItems="center"
                flexDirection="row"
                gap="8px"
                justifyContent="flex-end"
                paddingRight="16px"
              >
                <EllipsisTooltip
                  tooltip="Engagement rate of all identified potential BC members, based on outgoing and replied calls &
               emails, and communication recency over a selected period of time."
                >
                  <Typography
                    variant="body1"
                    noWrap={true}
                    sx={{
                      color: "#697184",
                    }}
                  >
                    Engagement
                  </Typography>
                </EllipsisTooltip>
              </Stack>
            }
            total_calls={
              <Typography
                variant="body1"
                noWrap={true}
                sx={{
                  color: "#697184",
                }}
              >
                Made
              </Typography>
            }
            total_calls_responded={
              <Typography
                variant="body1"
                noWrap={true}
                sx={{
                  color: "#697184",
                }}
              >
                Replied
              </Typography>
            }
            total_emails={
              <Typography
                variant="body1"
                noWrap={true}
                sx={{
                  color: "#697184",
                }}
              >
                Sent
              </Typography>
            }
            total_emails_responded={
              <Typography
                variant="body1"
                noWrap={true}
                sx={{
                  color: "#697184",
                }}
              >
                Replied
              </Typography>
            }
            total_meetings={
              <Typography
                variant="body1"
                noWrap={true}
                sx={{
                  color: "#697184",
                }}
              >
                Completed
              </Typography>
            }
          />
        </Stack>
      </Stack>
      {(data.length > 0 ? data : mockEngagementScore).map((item) => (
        <ItemList key={item.index} {...item} />
      ))}
    </Stack>
  );
};
